import React, { useEffect, useState } from "react";
import { api, fetchBranding, brandingLogoUrl } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Save, Warehouse, Store, ImagePlus, RefreshCw } from "lucide-react";

function BrandingCard() {
  const [name, setName] = useState("");
  const [hasLogo, setHasLogo] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBranding().then((b) => { setName(b.app_name); setHasLogo(b.has_logo); });
  }, []);

  const save = async () => {
    if (!name.trim() && !logoFile) return toast.error("Isi nama aplikasi atau pilih logo");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("app_name", name.trim());
      if (logoFile) fd.append("logo", logoFile);
      const { data } = await api.post("/branding", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await fetchBranding();
      setHasLogo(data.has_logo);
      setLogoFile(null);
      toast.success("Branding tersimpan — memuat ulang...");
      setTimeout(() => window.location.reload(), 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan branding");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="branding-card" className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center"><ImagePlus size={18} /></div>
        <h3 className="font-bold text-lg" style={{fontFamily:'Manrope'}}>Nama & Logo Aplikasi</h3>
      </div>
      <label className="block">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Nama Aplikasi</span>
        <input data-testid="branding-name" type="text" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} placeholder="GudangFlow" className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base" />
      </label>
      <div>
        <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Logo (PNG/JPG/WebP, maks 2 MB)</span>
        <div className="mt-1 flex items-center gap-3">
          {(hasLogo || logoFile) && (
            <img data-testid="branding-logo-preview" src={logoFile ? URL.createObjectURL(logoFile) : brandingLogoUrl()} alt="logo" className="w-14 h-14 rounded-xl object-cover border border-slate-200" />
          )}
          <label className="flex-1 h-14 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold text-slate-600 cursor-pointer hover:bg-slate-50">
            <ImagePlus size={16} /> {logoFile ? logoFile.name : "Pilih logo baru"}
            <input data-testid="branding-logo-input" type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => setLogoFile(e.target.files[0] || null)} />
          </label>
        </div>
      </div>
      <button data-testid="branding-save" onClick={save} disabled={saving} className="w-full h-12 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white font-bold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
        {saving ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />} Simpan Branding
      </button>
      <p className="text-xs text-slate-500">Nama & logo tampil di halaman login, header aplikasi, dan judul browser untuk semua pengguna.</p>
    </div>
  );
}

export default function SettingsPage() {
  const [form, setForm] = useState(null);

  useEffect(() => { api.get("/settings/work-hours").then(({ data }) => setForm(data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch("/settings/work-hours", {
        ...form,
        tolerance_minutes: parseInt(form.tolerance_minutes) || 0,
      });
      setForm(data);
      toast.success("Jam kerja tersimpan");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menyimpan"); }
  };

  if (!form) return <p className="text-slate-500">Memuat...</p>;

  const TimeField = ({ k, label, testid }) => (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</span>
      <input data-testid={testid} type="time" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base" />
    </label>
  );

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Pengaturan</p>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Pengaturan</h1>
        <p className="text-sm text-slate-600 mt-1">Branding aplikasi dan jam kerja untuk perhitungan keterlambatan otomatis.</p>
      </div>

      <BrandingCard />

      <form onSubmit={save} className="space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><Warehouse size={18} /></div>
            <h3 className="font-bold text-lg" style={{fontFamily:'Manrope'}}>Divisi Gudang Pusat</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TimeField k="gudang_in" label="Jam Masuk (WIB)" testid="set-gudang-in" />
            <TimeField k="gudang_out" label="Jam Pulang (WIB)" testid="set-gudang-out" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Store size={18} /></div>
            <h3 className="font-bold text-lg" style={{fontFamily:'Manrope'}}>Divisi Cabang Kota</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TimeField k="cabang_in" label="Jam Masuk (WIB)" testid="set-cabang-in" />
            <TimeField k="cabang_out" label="Jam Pulang (WIB)" testid="set-cabang-out" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Toleransi Keterlambatan (menit)</span>
            <input data-testid="set-tolerance" type="number" min={0} value={form.tolerance_minutes ?? 0} onChange={(e) => setForm({ ...form, tolerance_minutes: e.target.value })} className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base" />
          </label>
          <p className="text-xs text-slate-500 mt-2 flex items-center gap-1"><Clock size={12} /> Contoh: jam masuk 08:00 + toleransi 10 menit → absen 08:11 dihitung telat 1 menit.</p>
        </div>

        <button data-testid="set-save" type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <Save size={18} /> Simpan Pengaturan
        </button>
      </form>
    </div>
  );
}
